<template>
  <div class="card mc-token-api">
    <div class="card-body">
      <!-- Table -->
      <FormulateForm
        name="editable-table-form"
        ref="formRef"
        class="table-responsive my-3 border border-1"
        v-model="formValues"
        @submit="handleSubmit"
        @failed-validation="handleFailedValidation"
      >
        <b-table
          :busy="isBusy"
          :items="items"
          :fields="computedFields"
          :per-page="limit"
          fixed
          show-empty
          class="editable-table"
        >
          <!-- Loading -->
          <template #table-busy>
            <div class="__busy">
              <b-spinner class="align-middle mb-2"></b-spinner>
              <strong>{{ $t('global.text.loading') }}...</strong>
            </div>
          </template>

          <!-- Empty Result -->
          <template #empty>
            <div class="text-center my-4">{{ $t('global.emptyTable') }}</div>
          </template>

          <!-- Slot: All Cells -->
          <template #cell()="{ index, field, value, item }">
            <template v-if="field.isEditable && isEditingRow == index">
              <FormulateInput
                v-bind="{ ...field }"
                :rowItem="item"
                :label="null"
                :type="field.type || 'text'"
                :disabled="isSubmitting"
                size="sm"
                validation-behavior="live"
                help="required"
                validation="required:trim"
                :ref="`${field.key}-ref`"
              />
            </template>

            <template v-else>
              {{ value }}
            </template>
          </template>

          <!-- Slot: Access Token Column -->
          <template #cell(access_token)="{ index, field, value, item }">
            <div class="mc-token-api__token-column">
              <div class="token-text">
                <template v-if="isEditingRow == index"> - </template>
                <template v-else-if="tokenShownList[item.access_token] == true">
                  <b-tooltip :target="`token-${index}`" placement="auto">
                    {{ value }}
                  </b-tooltip>
                  <span :id="`token-${index}`">{{ truncateCharacter(value, 40) }}</span>
                </template>
                <template v-else>
                  {{ hideTokenText }}
                </template>
              </div>

              <b-button
                variant="outline"
                class="btn"
                :disabled="isEditingRow !== null"
                @click="() => handleToggleVisibility(item.access_token)"
              >
                <i v-if="tokenShownList[item.access_token] == true" class="icon-outline_eye" />
                <i v-else class="icon-outline_eye-off" />
              </b-button>

              <b-button
                variant="outline"
                class="btn"
                :disabled="isEditingRow !== null"
                @click="() => handleCopyToken(item.access_token)"
              >
                <i class="icon-outline_copy" />
              </b-button>
            </div>
          </template>

          <!-- Slot: Created Date Column -->
          <template #cell(created)="{ value, index }">
            <div v-if="isEditingRow == index">-</div>
            <div v-else class="mc-token-api__created-column">
              <div class="date-wrapper">{{ formattedDate(value).date }}</div>
              <div class="time-wrapper">{{ formattedDate(value).time }}</div>
            </div>
          </template>

          <!-- Slot: Revoke Action Column -->
          <template #cell(revoke_action)="{ item }">
            <McButton
              class="revoke-button"
              variant="secondary-red"
              size="sm"
              :text="$t('setting.list.development.revoke')"
              :disabled="isEditingRow !== null"
              @click="() => handleClickRevoke(item)"
            />
          </template>

          <!-- Slot: Edit Action Column -->
          <template #cell(edit_action)="{ index }">
            <div v-if="isEditingRow == index">
              <b-button variant="outline" class="__accept-button btn" type="submit">
                <i class="mc-icon icon-fill_checkmark"></i>
              </b-button>

              <b-button variant="outline" class="__cancel-button btn" @click="handleEditCancel">
                <i class="mc-icon icon-outline_close"></i>
              </b-button>
            </div>
          </template>
        </b-table>
      </FormulateForm>

      <!-- Limit & Pagination -->
      <div class="d-flex flex-wrap flex-md-nowrap">
        <div class="mc-limit">
          <div class="__limit">
            <div>{{ $t('global.show') }}</div>
            <b-form-select
              class="form-select form-select-sm __limit--dropdown"
              size="sm"
              :disabled="disabledGlobal"
              :options="limitOptions"
              :value="limit"
              @input="handleLimitChange"
            >
            </b-form-select>
            <div>{{ $t('global.text.data') }}</div>
          </div>

          <div class="__info" v-if="totalCount === 0">
            <strong>{{ totalCount }}</strong> {{ $t('global.object.of') }}
            <strong>{{ totalCount }}</strong>
            {{ $t('global.text.data') }}
          </div>

          <div class="__info" v-else>
            <strong>{{ startData }}</strong> - <strong>{{ endData }}</strong>
            {{ $t('global.object.of') }} <strong>{{ totalCount }}</strong>
            {{ $t('global.text.data') }}
          </div>
        </div>
        <div class="mc-pagination-container">
          <b-pagination
            :disabled="disabledGlobal"
            :per-page="limit"
            :total-rows="totalCount"
            :value="page"
            @change="handlePageChange"
          ></b-pagination>
        </div>
      </div>
    </div>

    <b-modal
      class="mc-modal"
      id="mc-modal-revoke"
      ref="mc-modal-revoke"
      title="Lepas Akses Token API"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p>
          Apakah Anda yakin ingin melepas akses token API
          <strong>{{ selectedRevokeItem.label }}</strong> secara permanen?
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            class="me-2"
            :disabled="isRevoking"
            :text="$t('global.action.cancel')"
            @click="() => handleCancelRevoke(cancel)"
          />
          <McButton
            variant="primary"
            text="Lepas Akses"
            :disabled="isRevoking"
            @click="handleRevokeAccess"
          />
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment'
import McButton from '@/components/McButton'
import { Helper } from '@/mixin/helper'

import * as ApiToken from '@/services/rest-api/lib/setting/api-token.js'
import listFields from '../../lib/fields/list-v2-fields'

export default {
  components: {
    McButton,
  },
  mixins: [Helper],
  data() {
    return {
      isEditingRow: null,
      isAdding: false,
      isSubmitting: false,
      isRevoking: false,
      isBusy: true,
      limit: 10,
      page: 1,
      totalCount: 0,
      formValues: {},
      items: [],
      limitOptions: [10, 25, 50, 100],

      selectedRevokeItem: {},
      tokenShownList: {},
    }
  },
  computed: {
    computedFields() {
      const fields = listFields()
      if (this.isEditingRow != null) {
        return [
          ...fields,
          {
            key: 'edit_action',
            label: '',
            thClass: 'mc-action-col-head',
            tdClass: 'mc-action-cell',
          },
        ]
      }

      return fields
    },
    disabledGlobal() {
      return false
    },
    startData() {
      return this.limit * this.page - this.limit + 1
    },
    endData() {
      const _endData = this.limit * this.page
      return this.totalCount < _endData ? this.totalCount : _endData
    },
    hideTokenText() {
      return '************************************************************'
    },
  },
  async mounted() {
    const opts = {
      page: this.page,
      limit: this.limit,
    }
    await this.getData(opts)
  },
  methods: {
    HandleAdd() {
      if (this.isEditingRow != null) return
      this.isEditingRow = 0
      this.isAdding = true

      if (this.items.length == 0) {
        this.items = [{ id: 0 }]
        return
      }

      this.items = [{ id: 0 }, ...this.items]
    },
    async getData(opts) {
      this.isBusy = true

      const res = await ApiToken.get(opts)

      this.items = res.data.pagination_result || []
      this.page = res.metadata.page
      this.totalCount = res.metadata.total_count
      this.isBusy = false
    },
    formattedDate(value) {
      const momentFormat = moment(value)

      return {
        date: momentFormat.format('DD MMM YYYY'),
        time: momentFormat.format('hh:mm'),
      }
    },
    async handleLimitChange(_limit) {
      this.limit = _limit

      await this.getData({
        limit: _limit,
        page: 1,
      })
    },
    async handlePageChange(newPage) {
      const opts = {
        page: newPage,
        limit: this.limit,
      }
      await this.getData(opts)
    },
    handleToggleVisibility(token) {
      this.$set(this.tokenShownList, token, !this.tokenShownList[token])
    },
    handleCopyToken(token) {
      navigator.clipboard.writeText(token)
      this.$mcToast.success('Token API berhasil disalin.')
    },
    handleClickRevoke(item) {
      this.selectedRevokeItem = item
      this.$refs['mc-modal-revoke'].show()
    },
    handleCancelRevoke(cancelMethod) {
      this.selectedRevokeItem = {}
      cancelMethod()
    },
    async handleRevokeAccess() {
      this.isBusy = true
      this.isRevoking = true
      const res = await ApiToken.revoke({ token: this.selectedRevokeItem.access_token })

      this.isRevoking = false
      this.$refs['mc-modal-revoke'].hide()

      if (res.errors) return

      this.page = 1
      await this.getData({
        limit: this.limit,
        page: this.page,
      })

      this.$mcToast.success('Akses token API berhasil dilepas.')
    },
    handleEditCancel() {
      if (this.isSubmitting) return
      if (this.isAdding) {
        const [, ...newItems] = this.items
        this.items = newItems
        this.isAdding = false
      }

      this.isEditingRow = null
    },
    async handleSubmit(data) {
      if (this.isSubmitting) {
        this.$mcToast.warning('Mohon tunggu')
        return
      }

      this.isSubmitting = true

      const res = await ApiToken.store(data)
      if (res.errors) {
        this.isSubmitting = false
        return
      }
      const newData = Object.assign({}, res, { created: new Date(), label: data.label })

      this.items.splice(this.isEditingRow, 1, newData)

      this.isEditingRow = null
      this.isAdding = false
      this.isSubmitting = false

      this.$mcToast.success('Token API berhasil dibuat.')
    },
    handleFailedValidation(_errors) {
      return this.$mcToast.error('Terdapat kesalahan format pada input anda')
    },
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
</style>
